import React, { useCallback, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Box, Button, Typography } from '@mui/material';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';
import { DataGridPro, useGridApiRef, GridCellParams, MuiEvent } from '@mui/x-data-grid-pro';
import { omit } from 'lodash';

import { useTranslation } from 'services/localization';
import { notUndefined } from 'common/utils/common';
import { moveElementLeft, moveElementRight } from 'common/utils/arrays';
import { updateLocaleField } from 'common/utils/languageUtils';
import { newFirestoreId } from 'common/utils/dataMigrations';
import useDebouncedForm from 'common/hooks/useDebouncedForm';

import { ProductVariant } from 'common/modules/inventory/types';
import { LocaleField } from 'common/types';
import { VariantProperty } from 'common/modules/inventory/types';

import * as ShopSelectors from 'selectors/ShopSelectors';
import * as ActiveProductSelectors from 'selectors/ActiveProductSelectors';
import * as ActiveProductActions from 'actions/ActiveProductActions';

import ColumnMenu from './components/ColumnMenu';
import DeleteColumnDialog from './components/DeleteColumnDialog';
import RenameColumnDialog from './components/RenameColumnDialog';
import DeleteRowsDialog from './components/DeleteRowsDialog';
import NewVariantDialog from './components/NewVariantDialog';
import AddColumnDialog from './components/AddColumnDialog';

import { getFixedColumnsStart, getDynamicColumns, getActionsColumn } from './columns';
import { getVariantsRows } from './rows';
import { getDataGridLocales } from 'services/localization/getDataGridLocales';
import HasFeature from 'components/features/HasFeature';
import AddOnPromoPage from 'components/features/promos/AddOnPromoPage';
import { GridCellEditCommitParams } from '@mui/x-data-grid';

const VariantsTable = () => {
	const classes = useStyles();
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const product = useSelector(ActiveProductSelectors.productDataWithChanges);
	const defaultLanguage = useSelector(ShopSelectors.activeShopDefaultLanguage);
	const isEditingDisabled = useSelector(ActiveProductSelectors.isEditingDisabled);

	const [selectedRowIds, setSelectedRowIds] = useState<string[]>([]);
	const [columnRenameId, setColumnRenameId] = useState<string | null>(null);
	const [columnDeleteId, setColumnDeleteId] = useState<string | null>(null);
	const [isDeleteRowsDialogOpen, setIsDeleteRowsDialogOpen] = useState(false);
	const [isAddNewVariantDialogOpen, setIsAddNewVariantDialogOpen] = useState(false);
	const [isAddNewColumnDialogOpen, setIsAddNewColumnDialogOpen] = useState(false);
	const [pageSize, setPageSize] = useState<number>(50);
	const apiRef = useGridApiRef();

	const { state, editState } = useDebouncedForm({
		memoizedInitialState: useMemo(
			() => ({
				variants: product.variants!,
			}),
			[product],
		),
		memoizedOnDebounce: useCallback(
			(changes) => {
				dispatch(ActiveProductActions.editLocalProduct(changes));
			},
			[dispatch],
		),
	});

	const rentalsEnabled = !!product.rentals?.enabled;
	const salesEnabled = !!product.sales?.enabled;

	const moveRow = (id: string, direction: 1 | -1) => {
		const fieldIdx = options.findIndex((field) => field.id === id);
		if (fieldIdx > -1) {
			const updatedOptions =
				direction === 1 ? moveElementRight(options, fieldIdx) : moveElementLeft(options, fieldIdx);
			updateState({ updatedOptions });
		}
	};

	const options = state.variants?.options ?? [];
	const properties = state.variants?.properties ?? [];

	const fixedColumnsStart = getFixedColumnsStart(t);
	const dynamicColumns = getDynamicColumns(properties, isEditingDisabled);
	const actionsColumn = getActionsColumn({
		apiRef,
		actions: {
			onHeaderClick: () => setIsAddNewColumnDialogOpen(true),
			moveRow,
		},
		t,
		isDisabled: isEditingDisabled,
	});

	const tableColumns = [...fixedColumnsStart, ...dynamicColumns, ...actionsColumn].filter(
		notUndefined,
	);

	const rows = getVariantsRows(options, product);

	const allRowsSelected = rows.length === selectedRowIds.length;

	const updateState = (args: {
		updatedOptions?: ProductVariant[];
		updatedProperties?: {
			id: string;
			name: LocaleField;
		}[];
	}) => {
		const { updatedOptions, updatedProperties } = args;
		editState({
			...state,
			variants: {
				options: updatedOptions ?? state.variants.options,
				properties: updatedProperties ?? state.variants.properties,
			},
		});
	};

	const moveColumn = (id: string, direction: 1 | -1) => {
		const fieldIdx = properties.findIndex((field) => field.id === id);
		if (fieldIdx > -1) {
			const updatedProperties =
				direction === 1
					? moveElementRight(properties, fieldIdx)
					: moveElementLeft(properties, fieldIdx);

			updateState({ updatedProperties });
		}
	};

	const deleteColumn = (id: string) => {
		const updatedProperties = properties.filter((p) => p.id !== id);
		const updatedOptions = options.map((o) => ({ ...o, values: omit(o.values, id) }));
		updateState({ updatedOptions, updatedProperties });
	};

	const handleDeleteRows = (ids: string[]) => {
		const updatedOptions = options.filter((p) => !ids.includes(p.id));
		updateState({ updatedOptions });
		setSelectedRowIds([]);
	};

	const addVariantProperty = (propertyName: string) => {
		if (!propertyName) {
			setIsAddNewColumnDialogOpen(false);
			return;
		}
		const newVariantPropertyName: LocaleField = updateLocaleField(
			undefined,
			propertyName,
			defaultLanguage,
		);

		const updatedProperties = [
			...properties,
			{ id: newFirestoreId(), name: newVariantPropertyName },
		];

		updateState({ updatedProperties });
		setIsAddNewColumnDialogOpen(false);
	};

	const addVariant = (
		props: { [key: string]: string },
		newVariantProperties?: VariantProperty[],
	) => {
		const updatedProperties = [
			...properties,
			...(!!newVariantProperties?.length ? newVariantProperties : []),
		];

		const newOption: ProductVariant = Object.entries(props).reduce(
			(acc, [key, value]) => {
				if (!!value) acc.values[key] = updateLocaleField(undefined, value, defaultLanguage);
				return acc;
			},
			{
				id: newFirestoreId(),
				stockSources: null,
				rentals: {
					enabled: rentalsEnabled,
					priceIncrease: null,
				},
				sales: {
					enabled: salesEnabled,
					priceOverride: null,
				},
				values: {},
			},
		);
		const updatedOptions = [...options, newOption];
		updateState({ updatedOptions, updatedProperties });
	};

	const renameColumn = (id: string, updatedName: string) => {
		const updatedProperties = properties.map((p) =>
			p.id === id ? { ...p, name: updateLocaleField(undefined, updatedName, defaultLanguage) } : p,
		);
		updateState({ updatedProperties });
	};

	const handlePropertyEdit = (params: GridCellEditCommitParams) => {
		const { id: variantId, field: propertyId, value } = params;

		const updatedOptions = options.map((option) => {
			if (option.id !== variantId) return option;

			return {
				...option,
				values: {
					...option.values,
					[propertyId]: updateLocaleField(
						option.values[propertyId],
						value as string,
						defaultLanguage,
					),
				},
			};
		});

		updateState({ updatedOptions });
	};

	const disableMuiDefaultEvent = (params: GridCellParams, event: MuiEvent) => {
		event.defaultMuiPrevented = true;
	};

	const handleCellClick = useCallback(
		(params: GridCellParams, event) => {
			if (!params.isEditable) return;
			apiRef.current.setCellMode(params.id, params.field, 'edit');
		},
		[apiRef],
	);

	return (
		<HasFeature
			requiredFeature="VARIANTS"
			fallback={(failedChecks) => {
				return !failedChecks.user ? <AddOnPromoPage addOn="PRODUCT_VARIANTS" /> : null;
			}}
		>
			<Box
				mb={2}
				display="flex"
				flexDirection="column"
				alignItems="flex-end"
				justifyContent="flex-end"
			>
				{!!selectedRowIds.length ? (
					<>
						<Button
							variant="contained"
							color="primary"
							disabled={allRowsSelected}
							onClick={() => setIsDeleteRowsDialogOpen(true)}
						>
							{t('product.variantsTable.deleteRowsBtn', {
								selectedRowCount: selectedRowIds.length,
								defaultValue: 'Delete selected ({{selectedRowCount}})',
							})}
						</Button>
						{allRowsSelected && (
							<Typography variant="caption" sx={{ mt: 1 }} color="error">
								{t(
									'product.variantsTable.variantsDeleteWarning',
									'Product must have at least 1 variant',
								)}
							</Typography>
						)}
					</>
				) : (
					<Button
						variant="outlined"
						color="primary"
						onClick={() => setIsAddNewVariantDialogOpen(true)}
						className={classes.newVariantBtn}
						disabled={isEditingDisabled}
					>
						+ {t('newProductVariant', 'New product variant')}
					</Button>
				)}
			</Box>
			<div className={classes.paper}>
				<DataGridPro
					autoHeight
					apiRef={apiRef}
					className={classes.datagridRoot}
					density="standard"
					checkboxSelection
					rows={rows}
					columns={tableColumns}
					localeText={getDataGridLocales(t)}
					disableSelectionOnClick
					disableColumnReorder={true}
					disableColumnMenu={isEditingDisabled}
					isRowSelectable={() => !isEditingDisabled}
					pagination
					pageSize={pageSize}
					onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
					rowsPerPageOptions={[5, 25, 50]}
					selectionModel={selectedRowIds}
					onSelectionModelChange={(newSelectionModel) => {
						setSelectedRowIds(newSelectionModel.map((id) => id.toString()));
					}}
					components={{
						ColumnMenu: ColumnMenu,
					}}
					componentsProps={{
						columnMenu: {
							dynamicColumns: dynamicColumns.map((c) => c?.field).filter(notUndefined),
							openRenameColumnDialog: setColumnRenameId,
							openDeleteColumnDialog: setColumnDeleteId,
							moveColumn: moveColumn,
						},
						pagination: {
							openNewVariantDialog: () => setIsAddNewVariantDialogOpen(true),
						},
					}}
					onCellClick={handleCellClick}
					onCellDoubleClick={disableMuiDefaultEvent}
					onCellEditCommit={handlePropertyEdit}
				/>
			</div>
			{!!columnDeleteId && (
				<DeleteColumnDialog
					columnId={columnDeleteId}
					deleteColumn={deleteColumn}
					columnName={properties.find((p) => p.id === columnDeleteId)?.name.def ?? ''}
					onClose={() => setColumnDeleteId(null)}
				/>
			)}
			{!!columnRenameId && (
				<RenameColumnDialog
					columnId={columnRenameId}
					columnName={properties.find((p) => p.id === columnRenameId)?.name.def ?? ''}
					renameColumn={renameColumn}
					onClose={() => setColumnRenameId(null)}
				/>
			)}
			{!!isDeleteRowsDialogOpen && (
				<DeleteRowsDialog
					rowIds={selectedRowIds}
					onClose={() => setIsDeleteRowsDialogOpen(false)}
					deleteRows={handleDeleteRows}
				/>
			)}
			{!!isAddNewVariantDialogOpen && (
				<NewVariantDialog
					properties={properties}
					onClose={() => {
						setIsAddNewVariantDialogOpen(false);
					}}
					onSave={addVariant}
				/>
			)}
			{!!isAddNewColumnDialogOpen && (
				<AddColumnDialog
					onSave={addVariantProperty}
					onClose={() => setIsAddNewColumnDialogOpen(false)}
				/>
			)}
		</HasFeature>
	);
};

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		paper: {
			backgroundColor: theme.palette.background.paper,
			border: `1px solid ${theme.palette.border.paper}`,
			borderRadius: 4,
		},
		datagridRoot: {
			backgroundColor: theme.palette.background.paper,
			'& .MuiDataGrid-columnHeaders': {
				backgroundColor: theme.palette.background.secondary,
			},
			'& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within': {
				outline: 'none',
			},
			'& .MuiDataGrid-menuIcon': {
				visibility: 'visible',
				width: 'auto',
			},
			'& .MuiDataGrid-toolbar': {
				padding: theme.spacing(1),
			},
			'& .MuiDataGrid-columnSeparator': {
				display: 'none',
				color: theme.palette.border.table,
			},
			'& .MuiDataGrid-row': {
				borderBottom: `1px solid ${theme.palette.border.table}`,
				backgroundColor: theme.palette.background.paper,
				'& .MuiDataGrid-cell:focus-within, & .MuiDataGrid-cell:focus': {
					outline: 'none',
				},
			},
			'& .MuiDataGrid-row:hover': {
				backgroundColor: theme.palette.action.hover,
			},
			'& .MuiDataGrid-cell.MuiDataGrid-cell--editing': {
				boxShadow: 'none',
				outlineOffset: 'initial',
				padding: '0 10px',
				alignItems: 'center',
			},
			'& .MuiDataGrid-cell': {
				border: 'none',
			},
			'& .MuiDataGrid-columnHeaders + div': {
				//Forces scrollbar to table bottom
				minHeight: '100%',
			},
			'& .MuiDataGrid-virtualScroller': {
				//Forces scrollbar to table bottom
				minHeight: 'calc(100% - 56px) !important',
			},
		},
		newVariantBtn: {
			marginRight: theme.spacing(2),
			maxWidth: 280,
		},
	}),
);

export default VariantsTable;
